<template>
  <div class="cca-dashboard-page p-3">
    <b-card class="shadow-sm border-0 mb-4 bg-white rounded-lg">
      <template #header>
        <div class="d-flex align-items-center justify-content-between py-1">
          <h5 class="mb-0 text-primary font-weight-bold">
            <b-icon icon="bar-chart-fill" class="mr-2"></b-icon>
            CCA Dashboard
          </h5>
          <b-button variant="primary" @click="fetchData" :disabled="loading" class="px-3 shadow-sm font-weight-bold">
            <b-icon icon="arrow-clockwise" class="mr-1"></b-icon> Refresh Data
          </b-button>
        </div>
      </template>

      <!-- LOADING -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2 text-muted">ກຳລັງໂຫຼດຂໍ້ມູນ...</p>
      </div>

      <!-- EMPTY -->
      <div v-else-if="!allAssessments.length" class="text-center py-5 text-muted">
        <b-icon icon="inbox" font-scale="3" class="mb-3 d-block mx-auto text-muted"></b-icon>
        <p>ບໍ່ມີຂໍ້ມູນການປະເມີນຂີດຄວາມສາມາດຂອງຊຸມຊົນ.</p>
      </div>

      <div v-else>

        <!-- ===== OVERVIEW CARDS ===== -->
        <h3 class="section-heading mt-2 mb-3">ພາບລວມ</h3>
        <b-row class="mb-4">
          <b-col md="4">
            <b-card class="text-center border-0 shadow-sm h-100 rounded-lg overview-card">
              <div class="icon-circle bg-light-info mb-3 mx-auto">
                <b-icon icon="clipboard-data" font-scale="2" class="text-info"></b-icon>
              </div>
              <h4 class="font-weight-bold text-dark mb-1">{{ allAssessments.length }}</h4>
              <p class="text-muted small mb-0">ຈຳນວນແບບສຳຫຼວດທັງໝົດ</p>
            </b-card>
          </b-col>
          <b-col md="4">
            <b-card class="text-center border-0 shadow-sm h-100 rounded-lg overview-card">
              <div class="icon-circle bg-light-success mb-3 mx-auto">
                <b-icon icon="building" font-scale="2" class="text-success"></b-icon>
              </div>
              <h4 class="font-weight-bold text-dark mb-1">{{ uniqueSchools }}</h4>
              <p class="text-muted small mb-0">ຈຳນວນໂຮງຮຽນທີ່ສຳຫຼວດ</p>
            </b-card>
          </b-col>
          <b-col md="4">
            <b-card class="text-center border-0 shadow-sm h-100 rounded-lg overview-card">
              <div class="icon-circle bg-light-warning mb-3 mx-auto">
                <b-icon icon="map" font-scale="2" class="text-warning"></b-icon>
              </div>
              <h4 class="font-weight-bold text-dark mb-1">{{ uniqueDistricts }}</h4>
              <p class="text-muted small mb-0">ຈຳນວນເມືອງທີ່ສຳຫຼວດ</p>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 1: ທີ່ຕັ້ງ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 1: ທີ່ຕັ້ງ ແລະ ໂຮງຮຽນ</h3>
        <b-row>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຈຳນວນການສຳຫຼວດຕາມເມືອງ</h5>
              <apexchart type="bar" height="280"
                :options="charts.byDistrict.options"
                :series="charts.byDistrict.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ໂຮງຮຽນທີ່ສຳຫຼວດ (Top 10)</h5>
              <apexchart type="bar" height="280"
                :options="charts.bySchool.options"
                :series="charts.bySchool.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 2: ປະຊາກອນ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 2: ຂໍ້ມູນກ່ຽວກັບປະຊາກອນ</h3>
        <b-row>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ກຸ່ມພາສາທີ່ໃຊ້ຫຼາຍໃນບ້ານ (Q2.5)</h5>
              <apexchart type="donut" height="280"
                :options="charts.language.options"
                :series="charts.language.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຜູ້ນຳພາຈັດຕັ້ງວຽກອາຫານທ່ຽງ (Q2.7)</h5>
              <apexchart type="bar" height="280"
                :options="charts.leaderRoles.options"
                :series="charts.leaderRoles.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 3: ຄວາມສ່ຽງ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 3: ຂໍ້ມູນຄວາມສ່ຽງຂອງຊຸມຊົນ</h3>
        <b-row>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ລະດັບການເຂົ້າເຖິງຂອງບ້ານ (Q3.1)</h5>
              <apexchart type="pie" height="280"
                :options="charts.access.options"
                :series="charts.access.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ປະສົບໄພພິບັດໃນ 1 ປີຜ່ານມາ (Q3.7)</h5>
              <apexchart type="donut" height="280"
                :options="charts.disaster.options"
                :series="charts.disaster.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ປະເພດໄພພິບັດທີ່ເກີດຂຶ້ນ (Q3.8)</h5>
              <apexchart type="bar" height="280"
                :options="charts.disasterTypes.options"
                :series="charts.disasterTypes.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ການຂາດແຄນເຂົ້າໃນ 1 ປີຜ່ານມາ (Q3.13)</h5>
              <apexchart type="pie" height="280"
                :options="charts.rice.options"
                :series="charts.rice.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 4: ໂຄງລ່າງ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 4: ພື້ນຖານໂຄງລ່າງ</h3>
        <b-row>
          <b-col md="12">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ພື້ນຖານໂຄງລ່າງທີ່ໂຮງຮຽນມີ (Q4.1) — ຈຳນວນໂຮງຮຽນ</h5>
              <apexchart type="bar" height="260"
                :options="charts.infra.options"
                :series="charts.infra.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ສະຖານະການໃຊ້ນ້ຳຂອງໂຮງຮຽນ (Q4.4)</h5>
              <apexchart type="donut" height="280"
                :options="charts.water.options"
                :series="charts.water.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ໂຮງຮຽນໄດ້ຈັດຕັ້ງເຮັດສວນຄົວ (Q4.2)</h5>
              <apexchart type="pie" height="280"
                :options="charts.garden.options"
                :series="charts.garden.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ໂຮງຮຽນມີບັນຊີທະນາຄານທີ່ໃຊ້ງານ (Q4.10)</h5>
              <apexchart type="donut" height="280"
                :options="charts.bank.options"
                :series="charts.bank.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ໂຮງຮຽນລ້ຽງສັດສຳລັບອາຫານທ່ຽງ (Q4.3)</h5>
              <apexchart type="pie" height="280"
                :options="charts.livestock.options"
                :series="charts.livestock.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 5: ການປະກອບສ່ວນ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 5: ການປະກອບສ່ວນຈາກຊຸມຊົນ</h3>
        <b-row>
          <b-col md="8">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ປະເພດການປະກອບສ່ວນ (Q5.1)</h5>
              <apexchart type="bar" height="260"
                :options="charts.contribution.options"
                :series="charts.contribution.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="4">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຄວາມຖີ່ການແຕ່ງອາຫານ/ອາທິດ (Q5.10)</h5>
              <apexchart type="bar" height="260"
                :options="charts.cookFreq.options"
                :series="charts.cookFreq.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

        <!-- ===== SECTION 6: ຄພສບ ===== -->
        <h3 class="section-heading mt-5 mb-3">ພາກທີ 6: ໜ້າທີ່ ຄພສບ</h3>
        <b-row>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຄວາມຖີ່ການປະຊຸມ ຄພສບ ຕໍ່ປີ (Q6.1)</h5>
              <apexchart type="bar" height="280"
                :options="charts.meetingFreq.options"
                :series="charts.meetingFreq.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຄພສບ ມີການຈັດທຳບັນຊີ (Q6.2)</h5>
              <apexchart type="donut" height="280"
                :options="charts.hasAccounting.options"
                :series="charts.hasAccounting.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ຄພສບ ສາມາດຍົກຕົວຢ່າງບັນຫາໄດ້ (Q6.5)</h5>
              <apexchart type="pie" height="280"
                :options="charts.problemSolving.options"
                :series="charts.problemSolving.series">
              </apexchart>
            </b-card>
          </b-col>
          <b-col md="6">
            <b-card class="mb-3 chart-card">
              <h5 class="chart-title">ການສ້ອມແປງໂຄງລ່າງໃນ 6 ເດືອນ (Q6.9)</h5>
              <apexchart type="donut" height="280"
                :options="charts.repairStatus.options"
                :series="charts.repairStatus.series">
              </apexchart>
            </b-card>
          </b-col>
        </b-row>

      </div>
    </b-card>
  </div>
</template>

<script>
import axios from "axios";

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16',
  '#f97316', '#64748b'
];

// ຕາຕະລາງ label ສຳລັບ field ທີ່ໃຊ້ code value (ຕ້ອງ match cca_form.vue ທຸກ option)
const LABEL_MAPS = {
  q2_5: {
    lao_tai:      'ລາວໄຕ',
    mon_khmer:    'ມອນ-ຂະແມ',
    sino_tibetan: 'ຈີນ-ຕິເບດ',
    hmong_mien:   'ມົ້ງ-ມຽນ',
  },
  q2_7: {
    principal:    'ອຳນວຍການ',
    teacher:      'ຄູສອນ',
    village_head: 'ນາຍບ້ານ',
    womens_union: 'ສະຫະພັນແມ່ຍິງ',
    parents:      'ພໍ່ແມ່ນັກຮຽນ',
    vedc:         'ສະມາຊິກ ຄພສບ',
    other:        'ອື່ນໆ',
  },
  q3_1: {
    A: 'A: ຕົວເມືອງ',
    B: 'B: ຊົນນະບົດ (ມີເສັ້ນທາງ)',
    C: 'C: ຊົນນະບົດ (ບໍ່ມີເສັ້ນທາງ)',
  },
  q3_7:  { yes: 'ມີ', no: 'ບໍ່ມີ', dk: 'ບໍ່ຮູ້' },
  q3_8: {
    flood:     'ນ້ຳຖ້ວມ',
    drought:   'ແຫ້ງແລ້ງ',
    pest:      'ສັດຕູພືດ',
    price_rise:'ລາຄາສູງ',
    illness:   'ເຈັບປ່ວຍ',
    other:     'ອື່ນໆ',
  },
  q3_13: { shortage: 'ຂາດແຄນ', sufficient: 'ພຽງພໍ', dk: 'ບໍ່ຮູ້' },
  q4_1: {
    handwash:    'ບ່ອນລ້າງມື',
    toilet:      'ຫ້ອງນ້ຳ',
    dining:      'ບ່ອນກິນເຂົ້າ',
    kitchen:     'ເຮືອນຄົວ',
    storage:     'ສາງອາຫານ',
    water_source:'ແຫຼ່ງນ້ຳ',
  },
  q4_2:  { yes: 'ມີສວນຄົວ', no: 'ບໍ່ມີ' },
  q4_3:  { yes: 'ລ້ຽງສັດ', no: 'ບໍ່ລ້ຽງ' },
  q4_4: {
    year_round:    'ຕະຫຼອດສົກ',
    insufficient:  'ບໍ່ພຽງພໍ',
    not_drinkable: 'ດື່ມບໍ່ໄດ້',
    broken:        'ເປ່/ເສຍຫາຍ',
    no_source:     'ບໍ່ມີແຫຼ່ງນ້ຳ',
    dk:            'ບໍ່ຮູ້',
  },
  q4_10: { yes: 'ມີ', no: 'ບໍ່ມີ', dk: 'ບໍ່ຮູ້' },
  q5_1: {
    cash:     'ເງິນສົດ',
    food:     'ອາຫານ',
    labor:    'ແຮງງານ',
    material: 'ວັດສະດຸ',
    other:    'ອື່ນໆ',
  },
  q5_10: {
    all:  'ທຸກມື້',
    '4_5':'4 ໃນ 5 ວັນ',
    '3_5':'3 ໃນ 5 ວັນ',
    '2_5':'2 ໃນ 5 ວັນ',
    '1_5':'1 ໃນ 5 ວັນ',
    none: 'ບໍ່ໄດ້ແຕ່ງ',
  },
  q6_1: {
    more_monthly: '>1 ຄັ້ງ/ເດືອນ',
    monthly:      '1 ຄັ້ງ/ເດືອນ',
    per_term:     '1 ຄັ້ງ/ພາກ',
    yearly:       '1 ຄັ້ງ/ສົກ',
    never:        'ບໍ່ເຄີຍ',
  },
  q6_2:  { yes: 'ມີ', no: 'ບໍ່ມີ', dk: 'ບໍ່ຮູ້' },
  q6_5:  { yes: 'ສາມາດ', no: 'ບໍ່ສາມາດ' },
  q6_9: {
    yes:        'ໄດ້ສ້ອມ/ຍົກລະດັບ',
    no_needed:  'ບໍ່ຕ້ອງການ',
    no_pending: 'ຍັງຄ້າງຢູ່',
    dk:         'ບໍ່ຮູ້',
  },
};

export default {
  data() {
    return {
      loading: false,
      allAssessments: [],
      uniqueSchools: 0,
      uniqueDistricts: 0,
      charts: {
        byDistrict:    { series: [{ data: [] }], options: {} },
        bySchool:      { series: [{ data: [] }], options: {} },
        language:      { series: [], options: {} },
        leaderRoles:   { series: [{ data: [] }], options: {} },
        access:        { series: [], options: {} },
        disaster:      { series: [], options: {} },
        disasterTypes: { series: [{ data: [] }], options: {} },
        rice:          { series: [], options: {} },
        infra:         { series: [{ data: [] }], options: {} },
        water:         { series: [], options: {} },
        garden:        { series: [], options: {} },
        livestock:     { series: [], options: {} },
        bank:          { series: [], options: {} },
        contribution:  { series: [{ data: [] }], options: {} },
        cookFreq:      { series: [{ data: [] }], options: {} },
        meetingFreq:   { series: [{ data: [] }], options: {} },
        hasAccounting: { series: [], options: {} },
        problemSolving:{ series: [], options: {} },
        repairStatus:  { series: [], options: {} },
      },
    };
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const loginData = JSON.parse(localStorage.getItem("Login") || "{}");
        const headers = { Authorization: "Bearer " + loginData.login_hash };
        const base = this.$store.getters.getBaseUrl;

        // ດຶງທຸກໜ້າ (API limit = 30 ຕໍ່ page)
        let all = [];
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const res = await axios.get(`${base}/get_cca_data?offset=${offset}`, { headers });
          const items = res.data.items || [];
          all = all.concat(items);
          hasMore = items.length === 30;
          offset++;
        }

        this.allAssessments = all;
        this.processOverview();
        this.initCharts();
      } catch (err) {
        console.error("fetchData error:", err);
      } finally {
        this.loading = false;
      }
    },

    processOverview() {
      this.uniqueSchools   = new Set(this.allAssessments.map(r => r.schoolCode).filter(Boolean)).size;
      this.uniqueDistricts = new Set(this.allAssessments.map(r => r.district).filter(Boolean)).size;
    },

    // ນັບຄ່າ single-value field
    countSingle(field) {
      const map = LABEL_MAPS[field] || {};
      const counts = {};
      this.allAssessments.forEach(item => {
        const raw = item[field];
        if (raw === '' || raw == null) return;
        const label = map[raw] || raw;
        counts[label] = (counts[label] || 0) + 1;
      });
      return counts;
    },

    // ນັບຄ່າ array field (multi-select)
    countMulti(field) {
      const map = LABEL_MAPS[field] || {};
      const counts = {};
      this.allAssessments.forEach(item => {
        const arr = item[field];
        if (!Array.isArray(arr)) return;
        arr.forEach(v => {
          const label = map[v] || v;
          counts[label] = (counts[label] || 0) + 1;
        });
      });
      return counts;
    },

    // ສ້າງ chart ປະເພດ pie / donut
    makePie(counts, type = 'pie') {
      const labels = Object.keys(counts);
      const values = Object.values(counts);
      if (!labels.length) {
        return { series: [1], options: { labels: ['ບໍ່ມີຂໍ້ມູນ'], chart: { type }, colors: ['#e2e8f0'], legend: { position: 'bottom' } } };
      }
      return {
        series: values,
        options: {
          chart: { type, fontFamily: 'Noto Sans Lao, sans-serif', toolbar: { show: false } },
          labels,
          colors: COLORS,
          legend: { position: 'bottom', fontSize: '12px', fontFamily: 'Noto Sans Lao, sans-serif' },
          dataLabels: { enabled: true, formatter: v => Math.round(v) + '%' },
          ...(type === 'donut' ? { plotOptions: { pie: { donut: { size: '60%' } } } } : {}),
        },
      };
    },

    // ສ້າງ chart ປະເພດ bar (horizontal ຫຼື vertical)
    makeBar(counts, horizontal = true) {
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const labels  = entries.map(e => e[0]);
      const values  = entries.map(e => e[1]);
      if (!labels.length) {
        return { series: [{ data: [] }], options: { chart: { type: 'bar' }, xaxis: { categories: [] } } };
      }
      return {
        series: [{ name: 'ຈຳນວນ', data: values }],
        options: {
          chart: { type: 'bar', fontFamily: 'Noto Sans Lao, sans-serif', toolbar: { show: false } },
          plotOptions: {
            bar: { horizontal, borderRadius: 4, dataLabels: { position: horizontal ? 'top' : 'top' } }
          },
          dataLabels: {
            enabled: true,
            style: { fontSize: '11px', colors: ['#1e293b'] },
            offsetX: horizontal ? 8 : 0,
          },
          xaxis: { categories: labels, labels: { style: { fontSize: '12px', fontFamily: 'Noto Sans Lao, sans-serif' } } },
          yaxis: { labels: { style: { fontSize: '12px', fontFamily: 'Noto Sans Lao, sans-serif' } } },
          colors: [COLORS[0]],
          grid: { borderColor: '#f1f5f9' },
        },
      };
    },

    initCharts() {
      if (!this.allAssessments.length) return;

      // --- Section 1: ທີ່ຕັ້ງ ---
      const districtCounts = {};
      this.allAssessments.forEach(r => {
        const d = r.district || 'ບໍ່ລະບຸ';
        districtCounts[d] = (districtCounts[d] || 0) + 1;
      });
      this.charts.byDistrict = this.makeBar(districtCounts, true);

      const schoolCounts = {};
      this.allAssessments.forEach(r => {
        const s = r.schoolName || r.schoolCode || 'ບໍ່ລະບຸ';
        schoolCounts[s] = (schoolCounts[s] || 0) + 1;
      });
      const top10 = Object.fromEntries(
        Object.entries(schoolCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)
      );
      this.charts.bySchool = this.makeBar(top10, true);

      // --- Section 2: ປະຊາກອນ ---
      this.charts.language    = this.makePie(this.countSingle('q2_5'), 'donut');
      this.charts.leaderRoles = this.makeBar(this.countMulti('q2_7'), true);

      // --- Section 3: ຄວາມສ່ຽງ ---
      this.charts.access        = this.makePie(this.countSingle('q3_1'), 'pie');
      this.charts.disaster      = this.makePie(this.countSingle('q3_7'), 'donut');
      this.charts.disasterTypes = this.makeBar(this.countMulti('q3_8'), true);
      this.charts.rice          = this.makePie(this.countSingle('q3_13'), 'pie');

      // --- Section 4: ໂຄງລ່າງ ---
      this.charts.infra     = this.makeBar(this.countMulti('q4_1'), true);
      this.charts.water     = this.makePie(this.countSingle('q4_4'), 'donut');
      this.charts.garden    = this.makePie(this.countSingle('q4_2'), 'pie');
      this.charts.livestock = this.makePie(this.countSingle('q4_3'), 'pie');
      this.charts.bank      = this.makePie(this.countSingle('q4_10'), 'donut');

      // --- Section 5: ການປະກອບສ່ວນ ---
      this.charts.contribution = this.makeBar(this.countMulti('q5_1'), true);
      this.charts.cookFreq     = this.makeBar(this.countSingle('q5_10'), false);

      // --- Section 6: ຄພສບ ---
      this.charts.meetingFreq    = this.makeBar(this.countSingle('q6_1'), false);
      this.charts.hasAccounting  = this.makePie(this.countSingle('q6_2'), 'donut');
      this.charts.problemSolving = this.makePie(this.countSingle('q6_5'), 'pie');
      this.charts.repairStatus   = this.makePie(this.countSingle('q6_9'), 'donut');
    },
  },
};
</script>

<style scoped>
.cca-dashboard-page {
  background-color: #f4f6fa;
  min-height: 100vh;
  font-family: 'Noto Sans Lao', sans-serif;
}

.section-heading {
  font-size: 1rem;
  font-weight: 700;
  color: #1e3a5f;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.chart-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
}

.chart-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #2d6a9f;
  margin-bottom: 1rem;
  padding-left: 10px;
  border-left: 3px solid #3b82f6;
}

.icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-light-info    { background-color: rgba(23,162,184,0.10); }
.bg-light-success { background-color: rgba(16,185,129,0.10); }
.bg-light-warning { background-color: rgba(245,158,11,0.10); }

.overview-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
}
.overview-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(30,58,95,0.10) !important;
}

.font-weight-bold { font-weight: 700 !important; }
.text-dark { color: #1e293b !important; }
</style>
